import { Stack } from '@mui/material';
import type {
  BackupVault,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import React, { useEffect, useState } from 'react';

import { useWorkspace } from '@/contexts/useWorkspace';
import { isDateValid } from '@/utils/dateTime';
import dayjs from '@/utils/dayjs';

import { OtherSnapshotsTab } from './otherSnapshotsTab';
import { SnapshotDrawerHeader } from './snapshotDrawerHeader';
import type { SnapshotsOrigin } from './snapshotOriginToggleButtons';
import { SnapshotRow } from './snapshotRow';
import type { SnapshotsType } from './snapshotsTypesTabs';
import { VMSnapshotRow } from './vmSnapshotRow';

import { Panels } from '../../panels';

const RestorePanels: Partial<
  Record<
    ResourceType,
    | 'RestoreS3Wizard'
    | 'RestoreRDSWizard'
    | 'RestoreDynamoWizard'
    | 'RestoreMongoAtlasWizard'
    | 'RestoreEksNamespaceWizard'
    | 'RestoreAzureSqlDBWizard'
    | 'RestoreGcpCloudSqlWizard'
  >
> = {
  [ResourceType.AwsS3]: Panels.RestoreS3Wizard,
  [ResourceType.AzureStorageAccount]: Panels.RestoreS3Wizard,
  [ResourceType.GcpCloudStorageBucket]: Panels.RestoreS3Wizard,
  [ResourceType.AwsRds]: Panels.RestoreRDSWizard,
  [ResourceType.AwsDynamoDb]: Panels.RestoreDynamoWizard,
  [ResourceType.AtlasMongodbCluster]: Panels.RestoreMongoAtlasWizard,
  [ResourceType.AwsEksNamespace]: Panels.RestoreEksNamespaceWizard,
  [ResourceType.AzureSqlDatabase]: Panels.RestoreAzureSqlDBWizard,
  [ResourceType.GcpCloudSqlInstance]: Panels.RestoreGcpCloudSqlWizard,
};

interface RestoreSnapshotBarProps {
  vaults: BackupVault[];
  eonSnapshots: Snapshot[];
  hasProviderSnapsInDate: boolean;
  selectedDate: Date;
  entity: InventoryResource;
  onClose: () => void;
}

export const RestoreSnapshotDrawer = (props: RestoreSnapshotBarProps) => {
  const {
    vaults,
    eonSnapshots,
    hasProviderSnapsInDate,
    selectedDate,
    entity,
    onClose,
  } = props;
  const { rightPanel } = useWorkspace();
  const { setComponent } = rightPanel;
  const [selectedOrigin, setSelectedOrigin] = useState<SnapshotsOrigin>(
    hasProviderSnapsInDate ? 'other' : 'eon'
  );
  const [selectedType, setSelectedType] = useState<SnapshotsType>('full');
  const [selectedDateSnapshots, setSelectedDateSnapshots] = useState<
    Snapshot[]
  >([]);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState<string>('');

  const restorePanel = RestorePanels[entity.resourceType];

  useEffect(() => {
    const dateSnapshots = selectedDate
      ? (eonSnapshots ?? [])
          .filter((e) => {
            const date = isDateValid(e.pointInTime)
              ? e.pointInTime
              : e.createdTime;

            return dayjs.utc(date).isSame(dayjs.utc(selectedDate), 'date');
          })
          .sort((a, b) => {
            const dateA = isDateValid(a.pointInTime)
              ? a.pointInTime
              : a.createdTime;

            const dateB = isDateValid(b.pointInTime)
              ? b.pointInTime
              : b.createdTime;

            return dateB.getTime() - dateA.getTime();
          })
      : [];
    setSelectedDateSnapshots(dateSnapshots);
    setSelectedSnapshot(dateSnapshots[0]?.id || '');
    if (dateSnapshots.length <= 0) {
      setSelectedOrigin('other');
    }
  }, [selectedDate, eonSnapshots]);

  const shouldShowGroupButtons = !!(
    selectedDateSnapshots.length && hasProviderSnapsInDate
  );

  return (
    <Stack
      maxHeight='60%'
      data-testid='restore-snapshot-drawer'
      className='w-full'
      direction='column'
      boxShadow='0px 10px 30px 0px rgba(0, 0, 0, 0.2)'
    >
      <Stack minHeight='0' className='w-full' direction='column'>
        <SnapshotDrawerHeader
          selectedDate={selectedDate}
          shouldShowGroupButtons={shouldShowGroupButtons}
          // todo: decide if to show tabs base on the existence of DB snapshots
          // probably if selectedDateSnapshots have record_restore_supported prop on them.
          shouldShowTabs={false}
          onSnapshotTypeSelected={setSelectedType}
          selectedType={selectedType}
          onClose={onClose}
          selectedSnapshotType={'eon'}
          onSnapshotOriginSelected={setSelectedOrigin}
          selectedOrigin={selectedOrigin}
          provider={entity.cloudProvider}
        />
        <Stack
          direction='column'
          alignItems='center'
          width='100%'
          overflow='scroll'
          minHeight='0'
        >
          {selectedOrigin === 'other' &&
          shouldShowGroupButtons &&
          entity.resourceType === ResourceType.AwsEc2 ? (
            <OtherSnapshotsTab
              data-testid='other-snapshots-tab'
              resource={entity}
              selectedDate={selectedDate}
            />
          ) : (
            selectedType === 'full' &&
            selectedDateSnapshots.map((snap) => {
              if (restorePanel) {
                return (
                  <SnapshotRow
                    key={snap.id}
                    snap={snap}
                    vault={vaults.find((v) => v.id === snap.vaultId)}
                    isSelected={selectedSnapshot === snap.id}
                    onSelect={() => setSelectedSnapshot(snap.id)}
                    resource={entity}
                    snapshotSelection={selectedDateSnapshots.length > 1}
                    onRestore={() => {
                      setComponent({
                        panel: restorePanel,
                        props: {
                          resource: entity,
                          snapshot: snap,
                          originTab: 'eon-snapshots',
                        },
                      });
                    }}
                  />
                );
              }

              if (
                entity.resourceType === ResourceType.AwsEc2 ||
                entity.resourceType === ResourceType.AzureVirtualMachine ||
                entity.resourceType === ResourceType.AzureSqlVirtualMachine ||
                entity.resourceType === ResourceType.GcpComputeEngineInstance
              ) {
                return (
                  <VMSnapshotRow
                    resource={entity}
                    key={snap.id}
                    isSelected={selectedSnapshot === snap.id}
                    snap={snap}
                    vault={vaults.find((v) => v.id === snap.vaultId)}
                    onSelect={() =>
                      setSelectedSnapshot(
                        selectedSnapshot === snap.id ? '' : snap.id
                      )
                    }
                    snapshotSelection={selectedDateSnapshots.length > 1}
                  />
                );
              }
            })
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
